import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { WalletProvider } from '@advisa/wallet';
import { ViewportProvider } from './viewport';
import { useSetupGrowthbook } from './growthBook';
import { FloatingFormProvider } from '../../context/floating-form';
import store from '../store';
import ErrorBoundary from './error-boundary';
import initLogging from '../../../sentry-config';
import { brand as importedBrand } from '../localizations/current-locale';

import '@mantine/core/styles.css';

const brand = importedBrand === 'lanaPengarna' ? 'advisa' : importedBrand;
const capitalizedBrand = brand.charAt(0).toUpperCase() + brand.slice(1);

const initializeSentry = () => {
  initLogging();
};

const waitForCookiebot = (callback) => {
  const maxAttempts = 50;
  let attempts = 0;
  const intervalId = setInterval(() => {
    if (typeof window !== 'undefined' && (window.Cookiebot || attempts >= maxAttempts)) {
      clearInterval(intervalId);
      callback();
    }
    attempts += 1;
  }, 100);
};

const wrapWithProvider = () => {
  const Wrapped = ({ element }) => {
    const { growthbook } = useSetupGrowthbook();

    useEffect(() => {
      if (typeof window === 'undefined') return;

      waitForCookiebot(() => {
        if (window.Cookiebot?.consent?.statistics) {
          initializeSentry();
        }

        const handleConsentChange = () => {
          if (window.Cookiebot?.consent?.statistics) {
            initializeSentry();
          }
        };

        window.addEventListener('CookiebotOnAccept', handleConsentChange);
        return () => {
          window.removeEventListener('CookiebotOnAccept', handleConsentChange);
        };
      });
    }, []);

    return (
      <GrowthBookProvider growthbook={growthbook}>
        <WalletProvider brand={capitalizedBrand}>
          <ViewportProvider>
            <FloatingFormProvider>
              <Provider store={store}>
                <ErrorBoundary>
                  {element}
                </ErrorBoundary>
              </Provider>
            </FloatingFormProvider>
          </ViewportProvider>
        </WalletProvider>
      </GrowthBookProvider>
    );
  };

  Wrapped.propTypes = {
    element: PropTypes.node.isRequired,
  };

  return Wrapped;
};

export default wrapWithProvider;
